import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, MenuItem, Box, Typography, CircularProgress, Alert,
} from "@mui/material";
import { BookOnlineOutlined } from "@mui/icons-material";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import { fetchBookingPrefill, convertToBooking, BookingPrefill } from "./emailHubV3Api";

const EVENT_TYPES = ["wedding", "corporate", "birthday", "anniversary", "gala", "other"];

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: number;
  onCreated: (booking: any) => void;
}

const ConvertBookingModal: React.FC<Props> = ({ open, onClose, threadId, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<Record<string, any>>({
    customer_name: "", email: "", phone: "", organization: "",
    eventType: "", eventDate: "", expectedGuests: "", publicNotes: "",
  });

  useEffect(() => {
    if (open && threadId) {
      setLoading(true);
      setError("");
      fetchBookingPrefill(threadId)
        .then((data) => {
          setForm({
            customer_name: data.customer_name || "",
            email: data.email || "",
            phone: data.phone || "",
            organization: data.organization || "",
            eventType: data.eventType || "",
            eventDate: data.eventDate || "",
            expectedGuests: data.expectedGuests ?? "",
            publicNotes: data.publicNotes || "",
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, threadId]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.customer_name.trim()) { setError("Customer name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const booking = await convertToBooking({ ...form, thread_id: threadId });
      onCreated(booking);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to create booking.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
        <BookOnlineOutlined sx={{ color: "#d97706" }} /> Convert to Booking
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
              This will create a new banquet booking pre-filled with thread data.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Customer Name" value={form.customer_name}
                  onChange={handleChange("customer_name")} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Email" value={form.email}
                  onChange={handleChange("email")} fullWidth size="small" type="email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone" value={form.phone}
                  onChange={handleChange("phone")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Organization" value={form.organization}
                  onChange={handleChange("organization")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Event Type" value={form.eventType}
                  onChange={handleChange("eventType")} fullWidth size="small" select>
                  <MenuItem value="">—</MenuItem>
                  {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <AppDatePicker label="Event Date" value={form.eventDate}
                  onChange={(v) => setForm((prev) => ({ ...prev, eventDate: v }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Expected Guests" value={form.expectedGuests}
                  onChange={handleChange("expectedGuests")} fullWidth size="small" type="number" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" value={form.publicNotes}
                  onChange={handleChange("publicNotes")} fullWidth size="small"
                  multiline rows={3} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : <BookOnlineOutlined />}
          sx={{ background: "#d97706", "&:hover": { background: "#b45309" } }}>
          Create Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertBookingModal;
